import { useEffect, useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { HttpError } from '@rossum/api-client/errors';
import { Queue } from '@rossum/api-client/queues';
import { Header, HeaderTitle } from '@rossum/components/header/Header';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Chip, Divider, Paper, Stack, Typography } from '@rossum/ui/material';
import { useSchema } from '../../../business/schema/useSchema';
import AutomationDetail from '../../../containers/Queue/containers/AutomationDetail';
import { linebreak } from '../../../lib/formaterValues';
import { userSelector } from '../../../redux/modules/user/selectors';
import { FeatureToggles } from '../../feature-enablement-ui/FeatureToggles';
import {
  isFeatureEnabled,
  isFeatureGloballyEnabled,
} from '../../feature-enablement/featureEnablement';
import { labFeatures } from '../../feature-enablement/features';
import { getFlattenSchemaFields } from '../../schemas/getFlattenSchemaFields';
import { notify } from '../../toaster';
import { QueueSettingsBreadcrumbs } from '../components/QueueSettingsBreadcrumbs';
import { QueueSettingsTabs } from '../components/QueueSettingsTabs';
import { ConfigureAutomation } from './ConfigureAutomation';
import { AutomationDisabledAlert } from './core/components/AutomationDisabledAlert';
import { StoredOptimizationAlert } from './core/components/StoredOptimizationAlert';
import { useAutomationFlags } from './core/hooks/useAutomationFlags';
import { useGetSavedAutomationTargets } from './core/hooks/useGetSavedAutomationTargets';
import { AutomationInsights } from './insights/AutomationInsights';
import { useGetAutomationInsights } from './insights/hooks/useGetAutomationInsights';
import {
  AggregationPeriod,
  TransformedAutomationInsights,
} from './insights/types';
import {
  getAggregatedAutomationRates,
  getAggregatedErrorRates,
  getDateLocale,
} from './insights/utils';

type AutomationPageProps = {
  selectedQueue: Queue;
  parentPath: string;
  workspaceName: string;
};

export const AutomationPage = ({
  selectedQueue,
  parentPath,
  workspaceName,
}: AutomationPageProps) => {
  const intl = useIntl();
  const user = useSelector(userSelector);

  const {
    data: savedAutomationTargets,
    isLoading: isLoadingAutomationTargets,
  } = useGetSavedAutomationTargets({
    queueId: selectedQueue.id,
  });

  const { isAutomationV2Enabled, queueLevelFlags } = useAutomationFlags();
  const isConfidentAutomation =
    queueLevelFlags.isAutomationConfident(selectedQueue);

  const isUsingAssistant = queueLevelFlags.isUsingAssistant(selectedQueue);

  const [aggregationPeriod, setAggregationPeriod] =
    useState<AggregationPeriod>('weeks');

  // load schema to get the labels for the schema_ids
  const { data: queueSchema, isLoading: isLoadingSchema } = useSchema(
    selectedQueue.schema
  );

  const {
    data: automationInsights,
    isLoading: isLoadingAutomationInsights,
    fetchStatus,
    error,
  } = useGetAutomationInsights({
    queueId: selectedQueue.id,
    enabled: isAutomationV2Enabled,
  });

  const isLoading =
    isLoadingAutomationInsights ||
    isLoadingSchema ||
    fetchStatus === 'fetching';

  useEffect(() => {
    if (error instanceof HttpError) {
      notify.error(
        {
          title: intl.formatMessage({
            id: 'containers.settings.queues.queue.automation.errors.generic.title',
          }),
        },
        // the requests are taking a while and its expected that they open a new tab and do stuff while its loading
        // so lets make sure they see this toast by duration:Infinity
        { id: 'automation-setup-error', duration: Infinity }
      );
    }
  }, [error, intl]);

  const aggregatedAutomationData = useMemo<Record<
    AggregationPeriod,
    TransformedAutomationInsights
  > | null>(() => {
    if (!automationInsights) return null;

    // when every array in the object is empty we have no stats -> empty state
    const isAutomationStatsEmpty = [
      automationInsights.documentAutomationTimeseries,
      automationInsights.datapointStatistics,
      automationInsights.estimatedErrorRateTimeseries,
      automationInsights.documentBlockers,
    ].every(array => array.length === 0);

    if (isAutomationStatsEmpty) return null;

    const locale = getDateLocale(intl.locale);

    return {
      days: {
        ...automationInsights,
        aggregatedDocumentAutomationTimeseries:
          automationInsights.documentAutomationTimeseries,
        aggregatedEstimatedErrorRateTimeseries:
          automationInsights.estimatedErrorRateTimeseries,
      },
      weeks: {
        ...automationInsights,
        aggregatedDocumentAutomationTimeseries: getAggregatedAutomationRates({
          aggregationPeriod: 'weeks',
          documentAutomationTimeseries:
            automationInsights.documentAutomationTimeseries,
          locale,
        }),
        aggregatedEstimatedErrorRateTimeseries: getAggregatedErrorRates({
          aggregationPeriod: 'weeks',
          estimatedErrorRateTimeseries:
            automationInsights.estimatedErrorRateTimeseries,
          locale,
        }),
      },
      months: {
        ...automationInsights,
        aggregatedDocumentAutomationTimeseries: getAggregatedAutomationRates({
          aggregationPeriod: 'months',
          documentAutomationTimeseries:
            automationInsights.documentAutomationTimeseries,
          locale,
        }),
        aggregatedEstimatedErrorRateTimeseries: getAggregatedErrorRates({
          aggregationPeriod: 'months',
          estimatedErrorRateTimeseries:
            automationInsights.estimatedErrorRateTimeseries,
          locale,
        }),
      },
    };
  }, [automationInsights, intl.locale]);

  const transformedAutomationData = aggregatedAutomationData
    ? aggregatedAutomationData[aggregationPeriod]
    : null;

  const schemaMap = useMemo(() => {
    if (!queueSchema || !queueSchema.content) return {};
    return getFlattenSchemaFields(queueSchema.content);
  }, [queueSchema]);

  const featureConfig = useMemo(() => {
    const userLabConfig = user.uiSettings?.labConfig || {};
    return {
      'mat-317-automation-settings-v2': {
        enabled: isFeatureEnabled(
          'mat-317-automation-settings-v2',
          userLabConfig
        ),
        askForFeedback:
          !userLabConfig['mat-317-automation-settings-v2']?.sentFeedback,
      },
    };
  }, [user]);

  const feature = R.prop(labFeatures, 'mat-317-automation-settings-v2');

  const isEnabledInUnleash = isFeatureGloballyEnabled(
    'mat-317-automation-settings-v2'
  );

  const translationKey = isAutomationV2Enabled ? 'newAutomation' : 'automation';

  const headerTitle = intl.formatMessage({
    id: 'containers.settings.queues.queue.automation',
  });

  return (
    <PageLayout
      fullWidth
      renderHeader={params => (
        <Header
          scrollableDivRef={params.scrollableDivRef}
          breadcrumbs={
            <QueueSettingsBreadcrumbs
              queue={selectedQueue}
              workspaceName={workspaceName}
              breadcrumbs={[
                {
                  label: intl.formatMessage({
                    id: 'containers.settings.queues.queue.automation',
                  }),
                },
              ]}
            />
          }
          tabs={
            <QueueSettingsTabs parentPath={parentPath} value="automation" />
          }
          title={
            isAutomationV2Enabled && isConfidentAutomation
              ? hasScrolled => {
                  return (
                    <Stack
                      direction="row"
                      sx={{ alignItems: 'center' }}
                      spacing={1}
                      useFlexGap
                    >
                      <HeaderTitle
                        hasScrolled={hasScrolled}
                        title={headerTitle}
                      />
                      <Chip
                        color="aurora"
                        label={intl.formatMessage({
                          id: 'containers.settings.automation.documentAutomation.confident.title',
                        })}
                      />
                    </Stack>
                  );
                }
              : headerTitle
          }
          description={intl.formatMessage({
            id: 'containers.settings.queues.queue.newAutomation.description',
          })}
          buttons={
            isAutomationV2Enabled
              ? [
                  <ConfigureAutomation
                    key="configure-automation"
                    queue={selectedQueue}
                    schemaMap={schemaMap}
                    queueSchema={queueSchema}
                    savedAutomationTargets={savedAutomationTargets}
                    isLoading={isLoadingAutomationTargets}
                    isAuroraQueue={queueLevelFlags.isAuroraQueue({
                      automationInsights,
                    })}
                  />,
                ]
              : []
          }
        />
      )}
    >
      <Stack
        sx={{
          flexGrow: 1,
          py: 3,
          px: 3,
          maxWidth: 1400,
          mx: 'auto',
          width: 1,
        }}
        spacing={3}
        useFlexGap
      >
        {isAutomationV2Enabled ? (
          <AutomationDisabledAlert queue={selectedQueue} />
        ) : null}
        {isAutomationV2Enabled && isUsingAssistant && savedAutomationTargets ? (
          <StoredOptimizationAlert
            savedAutomationTargets={savedAutomationTargets}
          />
        ) : null}
        {feature && isEnabledInUnleash ? (
          <Stack
            component={Paper}
            elevation={0}
            sx={{ py: 3 }}
            spacing={1}
            useFlexGap
          >
            <Stack sx={{ px: 4, width: 0.7 }} spacing={0.5} useFlexGap>
              <Typography variant="h5">
                {intl.formatMessage({
                  id: `containers.settings.queues.queue.${translationKey}.featureEnablement.title`,
                })}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {intl.formatMessage(
                  {
                    id: `containers.settings.queues.queue.${translationKey}.featureEnablement.description`,
                  },
                  { linebreak }
                )}
              </Typography>
            </Stack>
            <FeatureToggles
              features={[feature]}
              config={featureConfig}
              collectFeedback
            />
            <Divider />
          </Stack>
        ) : null}
        {isAutomationV2Enabled ? (
          <AutomationInsights
            isLoading={isLoading}
            transformedAutomationData={transformedAutomationData}
            isLoadingAutomationInsights={isLoadingAutomationInsights}
            schemaMap={schemaMap}
            aggregationPeriod={aggregationPeriod}
            setAggregationPeriod={setAggregationPeriod}
            queueId={selectedQueue.id}
            qualitySpotCheckEnabled={
              selectedQueue.qualitySpotCheckPercentage > 0
            }
          />
        ) : (
          <AutomationDetail
            selectedQueue={selectedQueue}
            parentPath={parentPath}
          />
        )}
      </Stack>
    </PageLayout>
  );
};
